import { supabase } from '../lib/supabase';

/**
 * Upload profile picture to Supabase Storage
 * @param {File} file - The image file to upload
 * @param {string} userId - The user ID
 * @returns {Promise<{success: boolean, url?: string, error?: Error}>}
 */
export const uploadProfilePicture = async (file, userId) => {
  try {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!validTypes?.includes(file?.type)) {
      throw new Error('Nevažeći tip fajla. Molimo koristite JPEG, PNG ili WebP format.');
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file?.size > maxSize) {
      throw new Error('Fajl je prevelik. Maksimalna veličina je 5MB.');
    }

    // Create unique file name
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${userId}/profile-${Date.now()}.${fileExt}`;

    // Upload to Supabase Storage
    const { data: uploadData, error: uploadError } = await supabase?.storage
      ?.from('profile-pictures')
      ?.upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      throw uploadError;
    }

    // Get public URL
    const { data: urlData } = supabase?.storage
      ?.from('profile-pictures')
      ?.getPublicUrl(fileName);

    if (!urlData?.publicUrl) {
      throw new Error('Nije moguće dobiti URL slike.');
    }

    return {
      success: true,
      url: urlData?.publicUrl
    };
  } catch (error) {
    console.error('Error uploading profile picture:', error);
    return {
      success: false,
      error: error
    };
  }
};

/**
 * Delete profile picture from Supabase Storage
 * @param {string} pictureUrl - The URL of the picture to delete
 * @param {string} userId - The user ID
 * @returns {Promise<{success: boolean, error?: Error}>}
 */
export const deleteProfilePicture = async (pictureUrl, userId) => {
  try {
    if (!pictureUrl) {
      return { success: true };
    }

    // Extract file path from URL
    const urlParts = pictureUrl?.split('/profile-pictures/');
    if (urlParts?.length < 2) {
      throw new Error('Nevažeći URL slike.');
    }

    const filePath = urlParts?.[1];

    // Verify the file belongs to the user
    if (!filePath?.startsWith(userId)) {
      throw new Error('Nemate dozvolu da obrišete ovu sliku.');
    }

    // Delete from storage
    const { error: deleteError } = await supabase?.storage
      ?.from('profile-pictures')
      ?.remove([filePath]);

    if (deleteError) {
      throw deleteError;
    }

    return { success: true };
  } catch (error) {
    console.error('Error deleting profile picture:', error);
    return {
      success: false,
      error: error
    };
  }
};

/**
 * Update user profile with new profile picture URL
 * @param {string} userId - User ID
 * @param {string} pictureUrl - New profile picture URL
 * @returns {Promise<{success: boolean, data?: Object, error?: Error}>}
 */
export const updateProfilePictureUrl = async (userId, pictureUrl) => {
  try {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.update({ profile_picture_url: pictureUrl })
      ?.eq('id', userId)
      ?.select()
      ?.single();

    if (error) throw error;

    // Convert snake_case to camelCase
    const profileData = {
      id: data?.id,
      fullName: data?.full_name,
      email: data?.email,
      phoneNumber: data?.phone,
      avatarUrl: data?.avatar_url,
      otherInfo: data?.other_info,
      valuebookLink: data?.valuebook_link,
      profilePictureUrl: data?.profile_picture_url,
      isVerified: data?.is_verified,
      rating: data?.rating,
      role: data?.role,
      totalPurchases: data?.total_purchases,
      totalSales: data?.total_sales,
      walletBalance: data?.wallet_balance,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };

    return {
      success: true,
      data: profileData
    };
  } catch (error) {
    console.error('Error updating profile picture URL:', error);
    return {
      success: false,
      error: error
    };
  }
};

/**
 * Get user profile by user ID
 * @param {string} userId - The user's ID
 * @returns {Promise<{success: boolean, data?: Object, error?: Error}>}
 */
export const getUserProfile = async (userId) => {
  try {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.select('*')
      ?.eq('id', userId)
      ?.single();

    if (error) throw error;

    // Convert snake_case to camelCase
    const profileData = {
      id: data?.id,
      fullName: data?.full_name,
      email: data?.email,
      phoneNumber: data?.phone,
      avatarUrl: data?.avatar_url,
      otherInfo: data?.other_info,
      valuebookLink: data?.valuebook_link,
      profilePictureUrl: data?.profile_picture_url,
      isVerified: data?.is_verified,
      rating: data?.rating,
      role: data?.role,
      totalPurchases: data?.total_purchases,
      totalSales: data?.total_sales,
      walletBalance: data?.wallet_balance,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };

    return {
      success: true,
      data: profileData
    };
  } catch (error) {
    console.error('Error getting user profile:', error);
    return {
      success: false,
      error: error
    };
  }
};

/**
 * Update user profile
 * @param {string} userId - The user's ID
 * @param {Object} profileData - Profile data with camelCase fields
 * @returns {Promise<{success: boolean, data?: Object, error?: Error}>}
 */
export const updateUserProfile = async (userId, profileData) => {
  try {
    // Convert camelCase to snake_case for database
    const dbData = {};
    
    if (profileData?.fullName !== undefined) dbData.full_name = profileData?.fullName;
    if (profileData?.phoneNumber !== undefined) dbData.phone = profileData?.phoneNumber;
    if (profileData?.avatarUrl !== undefined) dbData.avatar_url = profileData?.avatarUrl;
    if (profileData?.otherInfo !== undefined) dbData.other_info = profileData?.otherInfo;
    if (profileData?.valuebookLink !== undefined) dbData.valuebook_link = profileData?.valuebookLink;

    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.update(dbData)
      ?.eq('id', userId)
      ?.select()
      ?.single();

    if (error) throw error;

    // Convert snake_case to camelCase
    const updatedProfile = {
      id: data?.id,
      fullName: data?.full_name,
      email: data?.email,
      phoneNumber: data?.phone,
      avatarUrl: data?.avatar_url,
      otherInfo: data?.other_info,
      valuebookLink: data?.valuebook_link,
      profilePictureUrl: data?.profile_picture_url,
      isVerified: data?.is_verified,
      rating: data?.rating,
      role: data?.role,
      totalPurchases: data?.total_purchases,
      totalSales: data?.total_sales,
      walletBalance: data?.wallet_balance,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };

    return {
      success: true,
      data: updatedProfile
    };
  } catch (error) {
    console.error('Error updating user profile:', error);
    return {
      success: false,
      error: error
    };
  }
};